export interface PauseTypeVm {
  id: number;
  description: string;
  pauseNum: number;
  maxPauseTime: number | null;       // minutes (already divided by 60 in backend)
  // UI-only fields:
  maxPauseTimeString?: string;       // for editing the text
  nameError?: 'duplicatePauseName' | 'emptyPauseName';
  timeError?: 'pauseTime';
}

export interface PauseReasonsModel {
  pauseTypes: PauseTypeVm[] | null;
  canEdit: boolean;
  enableMaxTime: boolean;
}

export interface UpdatePauseTypesPostModel {
  deleted: number[];
  added: PauseTypeVm[];
  modified: PauseTypeVm[];
}

export interface UpdatePauseTypesResponse {
  error: boolean;
  message: string;
  pauses: PauseTypeVm[];
}

export interface AuditEntity {
  auditId: number;
  auditType: string;
  operation: string;
  messageId: string;
  customerKey: string;
  customerName: string;
  userId?: number | null;
  userName: string;
  fullUserName: string;
  displayName: string;
  logTime: string;          // DateTime → ISO string
  logTimeString: string;
  keyValue: string;
  message: string;
}

export interface LoginEntity {
  userId: number;
  timeLoggedin: string;      // DateTime → ISO string
  timeLoggedinString: string;
  clientType: string;
  operationSystem: string;
  webBrowser: string;
  ip: string;
  customerId: number;
  customerName: string;
  userName: string;
  userFullName: string;
  displayName: string;
  clientInfo: string;
  clientInfoFormatted: string;
  loginRejected: number;
  logInStatus: string;
}

export interface LogsSettingsModel {
  showChangeLog: boolean;
  showAccessLog: boolean;
  modulesModel: Module[];
}

export interface Module {
  moduleName: string;
  moduleKey: string;
}

export interface OperationEntity {
  operationName: string;
  operationKey: string;
}

export interface GetAccessListRequest {
  userName: string;
  dteFrom: string;
  dteTo: string;
  clientType: string;
  ip: string;
  loginMode: LogInMode; // or number if your LogInMode enum is numeric
}

// Mirror the C# enum exactly (order & values)
export enum LogInMode {
  Success = 0,
  Rejected = 1,
  All = 2
}

export interface GetLogsRequest {
  auditType: string;
  operation: string;
  userName: string;
  dteFrom: string;
  dteTo: string;
  moduleName: string;
  change: string;
}

export interface ChildProfile {
  id: number;
  description: string;
  usergroupId: number;
  usergroupName: string;
}

export enum CopyLevel {
  NONE = 0,
  USERGROUPS = 1,
  USERS = 2
}

export interface UsersGlobalProfilesModel {
  canEditMyCust: boolean;
  services: Service[];                    // Assuming Service exists elsewhere
  profiles: TemplateProfile[];
  serviceQualities: TemplateServiceQuality[];
}

export interface TemplateProfile {
  enableForChildren: CopyLevel;
  groupProfile: boolean | null;
  number: number | null;
  id: number | null;
  templateProfileId: number | null;
  name: string;
  skills: TemplateService[];
  hasChildProfile: boolean;
}
// User Groups Models
export interface UserGroup {
  id: number;
  name: string;
  isBillable?: boolean;
}

export interface UserGroupLong extends UserGroup {
  unblockableSize?: number;
}

export interface Service {
  id: number;
  number: string;
  description: string;
}

export interface UsergroupQuality {
  qualityId: number;
  categoryNumber: number;
  name: string;
  userLevel?: number;
  isFiltered?: boolean;
  isCategoryFiltered?: boolean;
  categoryName?: string;
}

export interface UsergroupServiceQuality {
  serviceId: number;
  serviceNumber: string;
  description: string;
  qualities: UsergroupQuality[];
  isSkillFilterActive?: boolean;
  isCategoryFilterActive?: boolean;
}

export interface TemplateQuality {
  qualityId: number;
  userLevel: number;
  categoryNumber: number;
  name: string;
}

export interface TemplateService {
  serviceId: number;
  qualities: TemplateQuality[];
}

export interface TemplateQuality {
  qualityId: number;
  userLevel: number;
  categoryNumber: number | null;
  categoryName: string;
  name: string;
  isFiltered: boolean;
  isCategoryFiltered: boolean;
}

export interface TemplateServiceQuality {
  serviceId: number | null;
  serviceNumber: string;
  description: string;
  isSkillFilterActive: boolean;
  isCategoryFilterActive: boolean;
  qualities: TemplateQuality[];
}

export interface ChangeProfile {
  name: string;
  qualities: ChangeQuality[];
}

export interface ChangeQuality {
  name: string;
  action: string;
}

export interface SaveProfileChanges {
  changeActiveAgents: boolean;
  isExcute: boolean;
  addedProfiles: TemplateProfile[];
  editedProfiles: TemplateProfile[];
  deletedProfiles: string[];
}

export interface Service {
  id: number;
  number: string;
  description: string;
  customerId: number;
  customerKey: string;
  customerDescription: string;
  applicationId: number;
  documentId: number;
  applicationDescription: string;
  configUri: string;
  isOwner: boolean;
}

export interface SaveTemplateProfilesResponse {
  error: boolean;
  message: string;
  returnObject: AffectedProfile[];
}

export interface AffectedProfile {
  profileTemplateId: number;
  profileTemplateName: string;
  action: string; // "d" or "u"
  updatedProfiles: ProfileUpdated[];
}

export interface ProfileUpdated {
  profileId: number;
  profileDescription: string;
  actorId: number;
  usergroupDescription: string;
}

export interface CheckForActiveAgentsResponse {
  isError: boolean;
  haveSkillsBeenChanged: boolean;
  numberOfActiveAgents: number;
}
// export interface TemplateProfile {
//   id: number;
//   templateProfileId?: number;
//   name: string;
//   number: number;
//   enableForChildren: number;
//   skills: TemplateService[];
// }

export interface UsergroupProfile {
  id?: number;
  name: string;
  number: number;
  enableForChildren: number;
  templateProfileId?: number;
  templateProfileName?: string;
  skills: UsergroupService[];
}

export interface UsergroupService {
  serviceId: number;
  qualities: UsergroupQuality[];
}

export interface UsersUsergroupsModel {
  isAdmin: boolean;
  canEditUsers: boolean;
  canAddUsergroup: boolean;
  services: Service[];
  allUserGroupsList: UserGroupLong[];
  templateProfiles: TemplateProfile[];
  usergroupServiceQualities: UsergroupServiceQuality[];
  allUsergroupAllProfiles?: any[];
  groupTypes: { [key: number]: string };
}

export interface SaveUsergroupChanges {
  usergroupId: number;
  newUsergroupName?: string;
  isBillable?: boolean;
  newIsBillable?: boolean;
  addedProfiles?: UsergroupProfile[];
  editedProfiles?: UsergroupProfile[];
  deletedProfiles?: number[];
  changeActiveAgents?: boolean;
}

export interface SaveUsergroupChangesResponse {
  validateUsergroupName?: boolean;
}

export interface CheckForActiveAgentsResponse {
  haveSkillsBeenChanged: boolean;
  numberOfActiveAgents: number;
}
